import React, { createContext, useContext, useState, useEffect } from 'react';
import { User } from '../types';
import { apiService } from '../services/api';
import socketService from '../services/socket';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (login: string, password: string) => Promise<{ success: boolean, requires2fa?: boolean, attemptId?: string }>;
  check2FA: (attemptId: string) => Promise<{ success: boolean, pending?: boolean }>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Token is in HttpOnly cookie, no need to check localStorage
      const response = await apiService.getCurrentUser();
      if (response.success && response.data) {
        setUser(response.data);
        socketService.connect();
      } else {
        setUser(null);
      }
    } catch (error: any) {
      // 401 on initial check is expected for non-authenticated users
      if (error?.response?.status === 401) {
        setUser(null);
        socketService.disconnect();
      } else {
        console.error('Auth check failed:', error);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (login: string, password: string): Promise<{ success: boolean, requires2fa?: boolean, attemptId?: string }> => {
    try {
      const response = await apiService.login(login, password);
      if (response.message === '2FA_REQUIRED' && (response.data as any)?.attempt_id) {
        return { success: true, requires2fa: true, attemptId: (response.data as any).attempt_id as string };
      }
      if (response.success && response.data) {
        setUser(response.data as User);
        socketService.connect();
        return { success: true };
      }
      return { success: false };
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        console.error('Login failed:', error);
      }
      return { success: false };
    }
  };

  const check2FA = async (attemptId: string): Promise<{ success: boolean, pending?: boolean }> => {
    try {
      const response = await apiService.check2FA(attemptId);
      if (response.message === 'PENDING') {
        return { success: false, pending: true };
      }
      if (response.success && response.data) {
        setUser(response.data as User);
        socketService.connect();
        return { success: true };
      }
      return { success: false };
    } catch (error: any) {
      return { success: false };
    }
  };

  const logout = async () => {
    try {
      await apiService.logout();
      setUser(null);
      socketService.disconnect();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, check2FA }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
